// TrendSparkline — shared no-data-safe trend component for operator metrics.
//
// THREE VISUAL STATES (never collapsed into one another):
//   no_data        — series absent/empty: source never recorded data.
//                    Renders a dashed mid-line, visually distinct from a flat
//                    zero line. An empty series reads "no data", not "zero".
//   insufficient   — fewer than MIN_TREND_POINTS buckets: not enough history
//                    to draw a trend. Renders individual dots, no connecting
//                    line.
//   real           — enough weekly buckets. Renders a sparkline polyline with
//                    an end-point marker. Null values in the series are treated
//                    as gaps (the point is skipped; the line jumps across it).
//
// PARTIAL CURRENT-PERIOD BUCKETS: callers are responsible for normalising the
// current (incomplete) bucket before passing it in. Raw partial buckets will
// draw a cliff that does not exist (a 6× misread was observed at W34; see step
// STEP-PWA-SPARKLINE-EXTRACT for the measured precedent).

export type TrendSeries = (number | null)[];
export type TrendState = "no_data" | "insufficient" | "real";

const MIN_TREND_POINTS = 4;

/**
 * Classify a series into one of three trend states.
 * - no_data      : series is null, undefined, or has length 0
 * - insufficient : 1–3 time buckets (not enough to infer a trend)
 * - real         : 4+ time buckets (including those with null values)
 */
export function classifyTrendState(
  series: TrendSeries | null | undefined,
): TrendState {
  if (series == null || series.length === 0) return "no_data";
  if (series.length < MIN_TREND_POINTS) return "insufficient";
  return "real";
}

/**
 * Compute SVG polyline geometry for a series.
 * Null entries are skipped (treated as gaps; the connecting line jumps).
 * Returns null when the series has no non-null values.
 */
export function computeSeriesPoints(
  series: TrendSeries,
  width: number,
  height: number,
): { pts: string; lastX: number; lastY: number } | null {
  const nonNull = series.filter((v): v is number => v != null);
  if (nonNull.length === 0) return null;

  const max = Math.max(...nonNull, 1);
  const min = Math.min(...nonNull, 0);
  const range = max - min || 1;
  const n = series.length;

  const points: string[] = [];
  let lastX = 0;
  let lastY = height;

  series.forEach((v, i) => {
    if (v == null) return;
    const x = (i / Math.max(1, n - 1)) * width;
    const y = height - ((v - min) / range) * height;
    points.push(`${x.toFixed(2)},${y.toFixed(2)}`);
    lastX = x;
    lastY = y;
  });

  if (points.length === 0) return null;
  return { pts: points.join(" "), lastX, lastY };
}

// ─── Component ─────────────────────────────────────────────────

export function TrendSparkline({
  series,
  height = 36,
  color = "var(--text)",
}: {
  series: TrendSeries | null | undefined;
  height?: number;
  color?: string;
}) {
  const w = 100;
  const h = height;
  const state = classifyTrendState(series);

  if (state === "no_data") {
    // Dashed mid-line — intentionally centred so it reads "nothing here" rather
    // than "zero". A flat real series draws its line at y=h (bottom edge).
    return (
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, display: "block" }}
        aria-label="no data"
      >
        <line
          x1="0"
          y1={h / 2}
          x2={w}
          y2={h / 2}
          stroke="var(--text-dim, #888)"
          strokeWidth="1"
          strokeDasharray="4 4"
          opacity="0.5"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    );
  }

  if (state === "insufficient") {
    const s = series!;
    const nonNull = s.filter((v): v is number => v != null);
    const max = Math.max(...nonNull, 1);
    const min = Math.min(...nonNull, 0);
    const range = max - min || 1;
    return (
      <svg
        viewBox={`0 0 ${w} ${h}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height, display: "block" }}
        aria-label="insufficient history"
      >
        {s.map((v, i) =>
          v == null ? null : (
            <circle
              key={i}
              cx={(i / Math.max(1, s.length - 1)) * w}
              cy={h - ((v - min) / range) * h}
              r="2.5"
              fill={color}
              opacity="0.6"
              vectorEffect="non-scaling-stroke"
            />
          ),
        )}
      </svg>
    );
  }

  // real — polyline with gap-skipping and end-point marker
  const geom = computeSeriesPoints(series!, w, h);
  if (!geom) return null;
  const { pts, lastX, lastY } = geom;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      style={{ width: "100%", height, display: "block" }}
      aria-label="trend"
    >
      <polyline
        points={pts}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={lastX}
        cy={lastY}
        r="2"
        fill={color}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
